import { Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { CHDevice, CHDeviceHelper } from "@app/project/model/ch-device";
import { Observable, throwError } from "rxjs";
import { catchError, filter, map, tap } from "rxjs/operators";
import {
    CLARE_HOME_METHODS,
    CLARE_SUMMARY_METHODS,
    ClareHomeRPCMethodNames,
    ClareHomeRPCServiceNames,
    HD_ACCESS_METHODS,
    HD_ADMIN_METHODS,
    PROTOCOL_ADAPTERS,
} from "../ClareHomeRpc.constants";
import { EquatorProxyService } from "../equator-proxy";

/* eslint-disable @typescript-eslint/naming-convention */
export interface DcoPayload {
    IN1?: string | number | boolean;
    IN2?: string | number | boolean;
    IN3?: string | number | boolean;
    IN4?: string | number | boolean;
    IN5?: string | number | boolean;
    IN6?: string | number | boolean;
    IN7?: string | number | boolean;
    IN8?: string | number | boolean;
}
/* eslint-enable @typescript-eslint/naming-convention */

@Injectable({
    providedIn: "root",
})
export class EquatorDevicesService {
    dbHelper = new CHDeviceHelper();

    constructor(readonly equatorProxyService: EquatorProxyService, private loggingService: AuditLogService) {}

    // There will be one of these in each equator-*.service class.
    // It supplies the JSON-RPC service name to the proxy.
    // Use this method in each method below.
    private call(
        uuid: string,
        method: ClareHomeRPCMethodNames,
        params: any[],
        service: ClareHomeRPCServiceNames = ClareHomeRPCServiceNames.HD_ACCESS,
        logInput: AuditLogInput = null
    ) {
        return this.equatorProxyService
            .makeClareHomeRpcRequest$(
                uuid, // uuid
                service, // service
                method, // method
                params // params
            )
            .pipe(tap(_ => this.saveAuditLog(logInput)));
    }

    private _escapeUuid(uuid: string): string {
        return uuid.replace(`(`, `\\(`).replace(`)`, `\\)`);
    }

    public add(uuid: string, protocolAdapter: string, deviceObj: CHDevice) {
        // need to export deviceObj before passing it on
        return this.call(
            uuid,
            HD_ADMIN_METHODS.ADD_HOME_DEVICE, // ClareHDAmin is a proxy for HDAdmin methods
            [protocolAdapter, deviceObj],
            protocolAdapter === PROTOCOL_ADAPTERS.CAMERAS ? ClareHomeRPCServiceNames.CLARE_HD_ADMIN : ClareHomeRPCServiceNames.HD_ADMIN
        ).pipe(
            catchError(e => {
                // if service name not found
                if (e.error?.code === -32601) {
                    return this.call(
                        uuid,
                        HD_ADMIN_METHODS.ADD_HOME_DEVICE,
                        [protocolAdapter, deviceObj],
                        ClareHomeRPCServiceNames.HD_ADMIN
                    );
                }
                return throwError(() => e);
            })
        );
    }

    public remove(uuid: string, uid: string, logInput: AuditLogInput = null) {
        return this.call(uuid, HD_ADMIN_METHODS.REMOVE_HOME_DEVICE, [uid], ClareHomeRPCServiceNames.HD_ADMIN, logInput);
    }

    public get(uuid: string, uid: string) {
        return this.getFilteredDevices(uuid, `(UID=` + this._escapeUuid(uid) + `)`);
    }

    public getAll(uuid: string) {
        return this.getFilteredDevices(uuid, "").pipe(
            filter(result => !!result),
            map(response =>
                response.reduce((devices, device: CHDevice) => ({ ...devices, [device.UID]: this.dbHelper.fromDbObject(device) }), {})
            )
        );
    }

    public getDCO(uuid: string, uid: string, dco: string): Observable<any> {
        return this.call(uuid, HD_ACCESS_METHODS.GET_DEVICE_CLASS_OBJECT, [uid, dco, true]);
    }

    public getDCOProperty<T = any>(uuid: string, uid: string, deviceClass: string, property: string): Observable<T> {
        return this.call(uuid, HD_ACCESS_METHODS.GET_DCO_PROPERTY, [uid, deviceClass, property]) as Observable<T>;
    }

    public setDCOProperty(uuid: string, uid: string, deviceClass: string, property: string, args: string | boolean): Observable<any> {
        return this.call(uuid, HD_ACCESS_METHODS.SET_DCO_PROPERTY, [uid, deviceClass, property, args]);
    }

    public setHomeDeviceProperty(uuid: string, uid: string, property: string, value: any): Observable<any> {
        return this.call(uuid, HD_ADMIN_METHODS.SET_HOME_DEVICE_PROPERTY, [uid, property, value || ""], ClareHomeRPCServiceNames.HD_ADMIN);
    }

    public setHomeDeviceProperties(uuid: string, uid: string, properties: Record<string, any>): Observable<any> {
        return this.call(uuid, HD_ADMIN_METHODS.SET_HOME_DEVICE_PROPERTIES, [uid, properties], ClareHomeRPCServiceNames.HD_ADMIN);
    }

    public getDCOs(uuid: string, uid: string): Observable<any> {
        return this.call(uuid, HD_ACCESS_METHODS.GET_DEVICE_CLASS_OBJECTS, [uid, true]);
    }

    public getFilteredDevices(uuid: string, ldapFilter: string): Observable<CHDevice[]> {
        return this.call(uuid, HD_ACCESS_METHODS.GET_HOME_DEVICES, [ldapFilter]);
    }

    public invokeDCOOperation(
        uuid: string,
        uid: string,
        deviceClass: string,
        operation: string,
        args?: DcoPayload,
        logInput?: AuditLogInput
    ): Observable<any> {
        return this.call(uuid, HD_ACCESS_METHODS.INVOKE_DCO_OPERATION, [uid, deviceClass, operation, args || {}], undefined, logInput);
    }

    /**
     *
     * @param {string} uuid controller uuid
     * @param {string} uid device uid
     * @param {sring} name device name
     * @returns
     */
    public setDeviceName(uuid: string, uid: string, name: string) {
        return this.call(uuid, HD_ADMIN_METHODS.SET_HOME_DEVICE_NAME, [uid, name], ClareHomeRPCServiceNames.HD_ADMIN);
    }

    public getDeviceClassesForMultipleDevices(uuid: string, deviceUids: string[], deviceClass: string, includeMetadata: boolean = false) {
        return this.call(
            uuid,
            CLARE_HOME_METHODS.GET_DEVICE_CLASS_FOR_DEVICES,
            [deviceUids, deviceClass, includeMetadata],
            ClareHomeRPCServiceNames.CLARE_HOME
        );
    }

    public getClareSummaryInfo(uuid: string) {
        return this.call(uuid, CLARE_SUMMARY_METHODS.GET_SUMMARY_INFO, [], ClareHomeRPCServiceNames.CLARE_SUMMARY);
    }

    private saveAuditLog(logData: AuditLogInput) {
        if (logData) {
            const { auditName, ...log } = logData;
            if (auditName && log) {
                this.loggingService.logEvent(auditName, log);
            }
        }
    }
}
